import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaMapMarkerAlt,
  FaStar,
  FaTrophy,
  FaFire,
  FaCalendar,
} from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../services/supabase';

function UserAuditModal({ user, onClose }) {
  const [stats, setStats] = useState(null);
  const [topBusinesses, setTopBusinesses] = useState([]);
  const [badges, setBadges] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchAuditData();
    }
  }, [user]);

  const fetchAuditData = async () => {
    try {
      // 1. Ümumi statistika
      const { count: totalCheckins } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Unikal business sayı
      const { data: checkinData } = await supabase
        .from('check_ins')
        .select('business_id')
        .eq('user_id', user.id);

      const uniqueBusinesses = new Set(checkinData?.map((c) => c.business_id)).size;

      // Friend count
      const { count: friendCount } = await supabase
        .from('friendships')
        .select('*', { count: 'exact', head: true })
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .eq('status', 'accepted');

      // Meetup count
      const { count: meetupCount } = await supabase
        .from('meetups')
        .select('*', { count: 'exact', head: true })
        .eq('creator_id', user.id);

      // Spin count
      const { count: spinCount } = await supabase
        .from('daily_spins')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Account age
      const accountAge = Math.floor(
        (new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24)
      );

      // Average per day
      const avgCheckinsPerDay = accountAge > 0 ? (totalCheckins / accountAge).toFixed(1) : 0;

      setStats({
        totalCheckins,
        uniqueBusinesses,
        friendCount,
        meetupCount,
        spinCount,
        points: user.points || 0,
        accountAge,
        avgCheckinsPerDay,
      });

      // 2. Top Businesses (ən çox check-in etdiyi business-lər)
      const { data: checkinsWithBusiness } = await supabase
        .from('check_ins')
        .select('business_id, businesses(name, city)')
        .eq('user_id', user.id);

      // Business-ə görə qruplaşdır
      const businessMap = {};
      checkinsWithBusiness?.forEach((checkin) => {
        const businessId = checkin.business_id;
        if (!businessMap[businessId]) {
          businessMap[businessId] = {
            businessId,
            name: checkin.businesses?.name || 'Unknown',
            city: checkin.businesses?.city || 'N/A',
            count: 0,
          };
        }
        businessMap[businessId].count++;
      });

      const topBusinessesList = Object.values(businessMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setTopBusinesses(topBusinessesList);

      // 3. Badges
      const { data: userBadges } = await supabase
        .from('user_badges')
        .select('*, badges(name, description, icon)')
        .eq('user_id', user.id);

      setBadges(userBadges || []);

      // 4. Recent Activity (son 10 check-in)
      const { data: recentCheckins } = await supabase
        .from('check_ins')
        .select('created_at, businesses(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      setRecentActivity(recentCheckins || []);
    } catch (error) {
      console.error('Error fetching user audit:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    if (seconds < 60) return `${seconds} saniyə əvvəl`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} dəqiqə əvvəl`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} saat əvvəl`;
    return `${Math.floor(seconds / 86400)} gün əvvəl`;
  };

  if (!user) return null;

  return (
    <>
      <div style={styles.overlay} onClick={onClose}></div>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <img
              src={
                user.avatar_url ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  user.full_name || 'User'
                )}&background=FF6B35&color=fff&size=80`
              }
              alt={user.full_name}
              style={styles.avatar}
            />
            <div>
              <h2 style={styles.title}>{user.full_name}</h2>
              <p style={styles.username}>@{user.username}</p>
              <p style={styles.email}>{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <FaTimes size={24} />
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Yüklənir...</p>
          </div>
        ) : (
          <div style={styles.content}>
            {/* Stats Grid */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <FaMapMarkerAlt size={32} color="#2196F3" />
                <div>
                  <p style={styles.statLabel}>Ümumi Check-ins</p>
                  <h3 style={styles.statValue}>{stats?.totalCheckins || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaStar size={32} color="#FFD700" />
                <div>
                  <p style={styles.statLabel}>Toplanan Xal</p>
                  <h3 style={styles.statValue}>{stats?.points || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.iconText}>🏢</div>
                <div>
                  <p style={styles.statLabel}>Ziyarət Etdiyi Yerlər</p>
                  <h3 style={styles.statValue}>{stats?.uniqueBusinesses || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.iconText}>👥</div>
                <div>
                  <p style={styles.statLabel}>Dostlar</p>
                  <h3 style={styles.statValue}>{stats?.friendCount || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.iconText}>🤝</div>
                <div>
                  <p style={styles.statLabel}>Meetup-lar</p>
                  <h3 style={styles.statValue}>{stats?.meetupCount || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.iconText}>🎰</div>
                <div>
                  <p style={styles.statLabel}>Spin Sayı</p>
                  <h3 style={styles.statValue}>{stats?.spinCount || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaFire size={32} color="#F44336" />
                <div>
                  <p style={styles.statLabel}>Orta Günlük Aktivlik</p>
                  <h3 style={styles.statValue}>{stats?.avgCheckinsPerDay || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaCalendar size={32} color="#9C27B0" />
                <div>
                  <p style={styles.statLabel}>Hesab Yaşı</p>
                  <h3 style={styles.statValue}>{stats?.accountAge || 0} gün</h3>
                </div>
              </div>
            </div>

            {/* Top Businesses Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.sectionTitle}>🏢 Ən Çox Ziyarət Etdiyi Yerlər (TOP 5)</h3>
              {topBusinesses.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={topBusinesses}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#999" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '10px',
                      }}
                      formatter={(value) => [`${value} check-in`, 'Ziyarət']}
                    />
                    <Bar dataKey="count" fill="#FF6B35" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p style={styles.noData}>Hələ check-in yoxdur</p>
              )}
            </div>

            {/* Badges */}
            <div style={styles.badgesSection}>
              <h3 style={styles.sectionTitle}>
                <FaTrophy color="#FFD700" /> Qazanılmış Badge-lər ({badges.length})
              </h3>
              {badges.length > 0 ? (
                <div style={styles.badgesGrid}>
                  {badges.map((userBadge) => (
                    <div key={userBadge.id} style={styles.badgeCard}>
                      <div style={styles.badgeIcon}>{userBadge.badges?.icon || '🏆'}</div>
                      <div>
                        <p style={styles.badgeName}>{userBadge.badges?.name}</p>
                        <p style={styles.badgeDesc}>{userBadge.badges?.description}</p>
                        <p style={styles.badgeDate}>
                          {new Date(userBadge.earned_at).toLocaleDateString('az-AZ')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={styles.noData}>Hələ badge yoxdur</p>
              )}
            </div>

            {/* Recent Activity */}
            <div style={styles.activitySection}>
              <h3 style={styles.sectionTitle}>🕐 Son Aktivliklər (10)</h3>
              {recentActivity.length > 0 ? (
                <div style={styles.activityList}>
                  {recentActivity.map((activity, index) => (
                    <div key={index} style={styles.activityItem}>
                      <div style={styles.activityIcon}>✓</div>
                      <div style={styles.activityInfo}>
                        <p style={styles.activityText}>
                          <strong>{activity.businesses?.name}</strong> - check-in
                        </p>
                        <p style={styles.activityTime}>
                          {getTimeAgo(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={styles.noData}>Aktivlik yoxdur</p>
              )}
            </div>

            {/* Performance Badge */}
            <div style={styles.performanceBadge}>
              <h4 style={styles.badgeTitle}>📊 İstifadəçi Performansı</h4>
              <div style={styles.badgeContent}>
                {stats?.totalCheckins >= 50 && (
                  <span style={styles.badge}>🔥 Aktiv İstifadəçi</span>
                )}
                {stats?.uniqueBusinesses >= 10 && (
                  <span style={styles.badge}>🌍 Explorer</span>
                )}
                {stats?.friendCount >= 10 && (
                  <span style={styles.badge}>👥 Social Butterfly</span>
                )}
                {stats?.points >= 1000 && (
                  <span style={styles.badge}>💎 Top Scorer</span>
                )}
                {badges.length >= 5 && (
                  <span style={styles.badge}>🏆 Badge Collector</span>
                )}
                {(!stats?.totalCheckins || stats.totalCheckins < 5) && (
                  <span style={styles.badgeWarning}>⚠️ Yeni İstifadəçi</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 2000,
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '95%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    overflowY: 'auto',
    zIndex: 2001,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    padding: '30px',
    borderBottom: '2px solid #F0F0F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 1,
  },
  headerContent: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #FF6B35',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  username: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '3px',
  },
  email: {
    fontSize: '14px',
    color: '#999',
  },
  closeBtn: {
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    flexShrink: 0,
  },
  content: {
    padding: '30px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '20px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  iconText: {
    fontSize: '32px',
  },
  statLabel: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '5px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  chartSection: {
    marginBottom: '30px',
    padding: '25px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  badgesSection: {
    marginBottom: '30px',
    padding: '25px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  badgesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '15px',
  },
  badgeCard: {
    display: 'flex',
    gap: '15px',
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  badgeIcon: {
    fontSize: '40px',
    flexShrink: 0,
  },
  badgeName: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '3px',
  },
  badgeDesc: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '5px',
  },
  badgeDate: {
    fontSize: '11px',
    color: '#999',
  },
  activitySection: {
    marginBottom: '30px',
    padding: '25px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  activityItem: {
    display: 'flex',
    gap: '15px',
    padding: '12px',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  activityIcon: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  activityInfo: {
    flex: 1,
  },
  activityText: {
    fontSize: '14px',
    color: '#1A1A2E',
    marginBottom: '3px',
  },
  activityTime: {
    fontSize: '12px',
    color: '#999',
  },
  performanceBadge: {
    padding: '25px',
    backgroundColor: '#E3F2FD',
    borderRadius: '12px',
    borderLeft: '5px solid #2196F3',
  },
  badgeTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '15px',
  },
  badgeContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  badge: {
    padding: '8px 15px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  badgeWarning: {
    padding: '8px 15px',
    backgroundColor: '#FF9800',
    color: '#fff',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  noData: {
    textAlign: 'center',
    color: '#999',
    padding: '40px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default UserAuditModal;